/**
 * Navbar.tsx
 * --------------------------------------------------------------------------
 * Header global del sitio en la versión definitiva de Ceciluz Arte.
 *
 * Composición visual (alineada con la referencia editorial de Terapias):
 *  - Sticky con backdrop-blur, fondo `cream-50/95`, borde inferior crema y
 *    sombra elev1 sutil. Línea dorada suave inferior para anclar la marca.
 *  - Logo a la izquierda: icono brand (`LotoIcon`) + texto "Ceciluz Arte"
 *    en serif + subtítulo en uppercase tracking wide.
 *  - Navegación central (sólo ≥1360 px): Inicio, Terapias, Tienda, Galería,
 *    Sobre mí, Eventos, Contacto. Link activo destacado con border-bottom
 *    dorado + `aria-current="page"`.
 *
 *    El corte NO es un breakpoint de Tailwind sino una medida: la barra
 *    completa necesita 1341 px reales (logo 238 + enlaces 716 + iconos y CTA
 *    339 + 48 de padding lateral). Con `lg` (1024) o `xl` (1280) la barra
 *    desborda horizontalmente; con ocho ítems de primer nivel no hay forma de
 *    que entre antes. Por debajo de 1360 px manda la hamburguesa, que ya
 *    existe y está pulida. Si algún día se recorta el menú, bajar este número
 *    a la nueva medida real.
 *  - Acciones derechas: búsqueda (placeholder, F8 conectará el palette),
 *    cuenta (Link `/cuenta`), carrito con badge dinámico, y CTA primaria
 *    "Reservar cita" con icono `Sparkles`.
 *  - Bajo ese ancho: logo + hamburguesa que abre `MobileMenu`.
 *
 * Contraste dinámico (F8 a11y) — el Navbar detecta cualquier elemento marcado
 * con `data-dark-bg="true"` (Section primary-700/900, PageHeader, hero de
 * EventoDetalle, etc.) que esté actualmente bajo la franja del Navbar y
 * conmuta sus colores a "modo claro sobre oscuro": texto crema, iconos crema,
 * CTA dorado, fondo transparente con leve velado oscuro. Esto garantiza
 * legibilidad cuando el usuario hace scroll y la sticky atraviesa el hero
 * violeta. Sin acoplarse a rutas — cualquier sección marcada lo activa.
 *
 * Sólo tokens del sistema. Cero hex literals. ARIA correcta:
 *  - `<header role="banner">`
 *  - `<nav aria-label="Navegación principal">`
 *  - `aria-current="page"` en el item activo
 */
import { useState, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, User, Search, Sparkles } from 'lucide-react';
import { Routes } from '../../lib/routes';
import { Button } from '../ui/Button';
import { cn } from '../../lib/cn';
import { MobileMenu } from './MobileMenu';
import { CartIconBadge } from './CartIconBadge';
import { GlobalSearchModal } from './GlobalSearchModal';
import { OrientacionBookingLauncher } from '../../features/booking/OrientacionBookingLauncher';

// Tipos exportados ----------------------------------------------------------

export interface NavItem {
  /** Etiqueta visible en el header. */
  label: string;
  /** Ruta destino (debe estar en `Routes`). */
  href: string;
}

export interface NavbarProps {
  /** Permite inyectar items custom; por defecto usa el set canónico. */
  items?: NavItem[];
}

/**
 * Items canónicos exportados para que `MobileMenu` (o cualquier test) reutilice
 * la misma fuente de verdad sin duplicar literales.
 *
 * Orden y labels (QA UI 2026-06-01):
 *   Inicio · Terapias · Tienda · Galería · Eventos · Acerca de mí · Recursos · Contacto
 *
 * Notas:
 *  - "Galería" entra al Navbar entre Tienda y Eventos para hacer descubrible
 *    el catálogo público (`/galeria`). El `MobileMenu` ya tenía mapeado el
 *    ícono (`ImageIcon`) en `NAV_ICON_BY_HREF`, así que el sheet móvil lo
 *    pinta correctamente sin cambios adicionales.
 *  - "Sobre mí" se renombra a "Acerca de mí" (la ruta `/sobre-mi` se mantiene
 *    intacta — no rompe deep-links existentes ni SEO).
 *  - "Recursos" es nueva — apunta al placeholder `/recursos` hasta que el
 *    contenido editorial esté listo.
 */
export const NAV_ITEMS: NavItem[] = [
  { label: 'Inicio', href: Routes.home },
  { label: 'Terapias', href: Routes.therapies },
  { label: 'Tienda', href: Routes.shop },
  { label: 'Galería', href: Routes.gallery },
  { label: 'Eventos', href: Routes.events },
  { label: 'Acerca de mí', href: Routes.about },
  { label: 'Recursos', href: Routes.resources },
  { label: 'Contacto', href: Routes.contact },
];

/**
 * Altura aproximada de la franja del Navbar (la mayor entre mobile h-20 y
 * desktop h-24). Se usa para decidir si un elemento marcado `data-dark-bg`
 * está actualmente "detrás" del Navbar.
 */
const NAVBAR_BAND_HEIGHT_PX = 96;

/**
 * Path del logo principal — servido desde `public/brand/` por Vite.
 * Si el archivo no existe (build inicial sin asset), el `<img>` muestra el
 * `alt`. Cambiar acá si renombrás el archivo o pasás a SVG.
 */
const BRAND_LOGO_SRC = '/brand/logo-ceciluz-arte.webp';

// Componente ----------------------------------------------------------------

export function Navbar({ items = NAV_ITEMS }: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  /** Controla el GlobalSearchModal (command palette). */
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  /** Reserva de la Orientación gratuita abierta desde "Reservar cita". */
  const [isReservaOpen, setIsReservaOpen] = useState(false);
  /** True cuando el Navbar sticky pasa por encima de una sección oscura. */
  const [isOverDark, setIsOverDark] = useState(false);
  const location = useLocation();

  // Cerramos el sheet mobile cada vez que cambia la ruta para evitar
  // que quede abierto después de navegar.
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Cerramos también el modal de búsqueda al navegar (cualquier resultado
  // seleccionado dispara navigate, y queremos que el modal desaparezca).
  // Doble seguridad: el handler `handleSelect` ya cierra, pero esto cubre el
  // caso de back-button del navegador después de elegir un resultado.
  useEffect(() => {
    setIsSearchOpen(false);
  }, [location.pathname]);

  // Contraste dinámico — observa scroll/resize/DOM y prende `isOverDark`
  // cuando cualquier elemento `[data-dark-bg="true"]` intersecta la franja
  // del Navbar (top viewport). Usa `requestAnimationFrame` para throttling
  // sin agregar dependencias.
  //
  // F8.3 — Primer evaluate va en `useLayoutEffect` para eliminar flicker:
  // sin esto, el primer paint mostraba el Navbar en modo light durante 1
  // frame aunque la página tuviera hero violeta. `useLayoutEffect` corre
  // después del commit pero ANTES del paint, así que el usuario ve el
  // Navbar con el modo correcto desde el primer pixel.
  useLayoutEffect(() => {
    // Evaluación síncrona inicial. Los hijos (Section, PageHeader, hero
    // manual) ya están en el DOM porque React commit-ea bottom-up.
    const nodes = document.querySelectorAll<HTMLElement>('[data-dark-bg="true"]');
    let overDark = false;
    for (const el of nodes) {
      const rect = el.getBoundingClientRect();
      if (rect.top <= NAVBAR_BAND_HEIGHT_PX && rect.bottom > 0) {
        overDark = true;
        break;
      }
    }
    setIsOverDark(overDark);
  }, [location.pathname]);

  useEffect(() => {
    let frame = 0;

    const evaluate = () => {
      frame = 0;
      const nodes = document.querySelectorAll<HTMLElement>('[data-dark-bg="true"]');
      let overDark = false;
      for (const el of nodes) {
        const rect = el.getBoundingClientRect();
        // El elemento está bajo el Navbar si cruza la franja superior:
        // su top entra o ya pasó por la franja, y aún no salió por arriba.
        if (rect.top <= NAVBAR_BAND_HEIGHT_PX && rect.bottom > 0) {
          overDark = true;
          break;
        }
      }
      setIsOverDark(overDark);
    };

    const schedule = () => {
      if (frame !== 0) return;
      frame = requestAnimationFrame(evaluate);
    };

    // Re-evaluación a los 100ms para capturar Sections que se hayan montado
    // tardíamente (lazy chunks Suspense, queries que insertan contenido).
    const tInit = window.setTimeout(schedule, 100);

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.clearTimeout(tInit);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [location.pathname]);

  // Helper: ¿el item corresponde a la ruta actual?
  const isActiveItem = (href: string) => {
    if (href === Routes.home) {
      return location.pathname === Routes.home;
    }
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  // Búsqueda global — abre el GlobalSearchModal (command palette).
  // El modal se monta condicionalmente abajo (`<GlobalSearchModal isOpen=...>`),
  // así que mientras está cerrado no dispara las 4 queries de fondo.
  const handleSearchClick = () => {
    setIsSearchOpen(true);
  };

  // ---------- Paletas dependientes del modo dark/light ---------------------

  /** Fondo del header.
   *
   *  - Modo dark (sobre secciones violetas con `data-dark-bg`):
   *    `bg-primary-900` opaco → contraste 18:1 con texto crema, pasa WCAG AA
   *    en todos los navegadores.
   *  - Modo light (estado por defecto sobre páginas cream):
   *    `bg-cream-50` **opaco** + shadow-elev2 + borde dorado.
   *    Anteriormente era `cream-50/95` con 5% de transparencia; el bleed
   *    de cards oscuras (Próximos eventos del Home, Destacada de Terapias,
   *    Footer global) se veía a través del navbar y rompía la legibilidad
   *    del logo/menú. La detección `data-dark-bg` cubre las secciones
   *    full-width violetas — para cards locales con gradiente oscuro
   *    (event cards), un navbar 100% opaco es la única solución sin
   *    parpadeo. El `backdrop-blur` se mantiene por consistencia con dark
   *    mode y no introduce penalización con bg opaco.
   */
  const headerClasses = isOverDark
    ? cn('bg-primary-900 backdrop-blur', 'border-b border-gold-500/40', 'shadow-elev1')
    : cn('bg-cream-50 backdrop-blur', 'shadow-elev2', 'border-b border-gold-500/35');

  /** Color base de los iconos action (search, user, hamburguesa). */
  const iconBtnClass = isOverDark
    ? 'text-cream-50 hover:bg-cream-100/15'
    : 'text-primary-700 hover:bg-cream-200';

  return (
    <>
      <header
        role="banner"
        className={cn(
          // Sticky en el tope, encima del contenido pero debajo de modales.
          'sticky top-0 left-0 right-0 z-sticky',
          // `w-full max-w-[100vw] box-border` blindan el header contra
          // overflow horizontal: en mobile, si algún hijo intentara empujar
          // al header más allá del viewport, esto lo contiene y evita que
          // la hamburguesa sea expulsada o cortada por el clip del <main>.
          // Como nada del header puede sobrepasar el viewport, no necesitamos
          // overflow-x-hidden en ningún ancestor — y eso preserva el sticky.
          'w-full max-w-[100vw] box-border',
          // Transición suave entre modos para no parpadear durante el scroll.
          'transition-colors duration-2 ease-out',
          headerClasses,
        )}
      >
        <nav
          aria-label="Navegación principal"
          // `w-full min-w-0` permite que el flex container se ajuste al
          // viewport sin que el contenido lo empuje más allá. Necesario
          // para que la hamburguesa nunca quede expulsada del lado derecho
          // cuando el logo es ancho en mobile (≤400 px).
          className="mx-auto flex h-20 w-full min-w-0 max-w-[1440px] items-center justify-between px-4 md:h-24 md:px-6"
        >
          {/* Bloque marca — imagen única del logo (eye + luna + loto +
              "Ceciluz Arte" + tagline + línea decorativa). Reemplaza la
              composición anterior de seal + texto para coincidir con el
              brand kit definitivo entregado por el cliente.
              En dark mode aplicamos `brightness(0) invert(1)` como fallback
              automático mientras no exista variante light del logo en
              `public/brand/logo-ceciluz-arte-light.webp`. */}
          <Link
            to={Routes.home}
            aria-label="Ceciluz Arte, ir al inicio"
            // `min-w-0 shrink` permite que el bloque del logo se reduzca
            // cuando el viewport es estrecho (≤400 px), liberando espacio
            // para que la hamburguesa siempre quepa al lado derecho.
            //
            // QA 2026-09-22: desde 1360 px va `shrink-0`. Antes el `shrink`
            // aplicaba en todos los breakpoints y entre ~1000 y 1023 px —con
            // los ocho enlaces, los tres iconos y el CTA compitiendo por el
            // ancho— el flex colapsaba el logo a 0 px: la marca simplemente
            // desaparecía del navbar. Medido: 238 px a 1366 px, 0 px a
            // 1009 px. Con la barra de escritorio movida a 1360 px el caso ya
            // no se da, pero fijar el `shrink-0` evita que vuelva si alguien
            // agrega un enlace más.
            className="group inline-flex min-w-0 shrink items-center min-[1360px]:shrink-0"
          >
            <img
              src={BRAND_LOGO_SRC}
              alt="Ceciluz Arte"
              // Altura responsive: ocupa ~80% del alto del navbar para dejar
              // respiración vertical, ancho `auto` mantiene proporción del PNG.
              // `max-w-full` permite que el img se reduzca con el `<Link>`
              // padre cuando éste se aprieta — en mobile, la altura se reduce
              // proporcionalmente y el aspect ratio se preserva (w-auto).
              className={cn(
                'h-14 w-auto max-w-full md:h-16 lg:h-20',
                'transition-[filter,opacity] duration-2 ease-out',
                'group-hover:opacity-90',
                // Fallback dark mode: invierte el PNG (navy → blanco, gold se
                // pierde como gris claro). Si subís el archivo "-light" lo
                // ideal es swappear el `src` por ruta, no usar filter.
                isOverDark && 'brightness-0 invert',
              )}
              decoding="async"
              draggable={false}
            />
          </Link>

          {/* Navegación central (sólo desktop). */}
          <ul className="hidden items-center gap-1 min-[1360px]:flex lg:gap-2">
            {items.map((item) => {
              const active = isActiveItem(item.href);
              // Item activo: en modo dark conserva acento dorado; en light
              // queda con texto morado + border dorado (igual que antes).
              const activeClasses = isOverDark
                ? 'text-gold-500 font-semibold border-gold-500'
                : 'text-primary-700 font-semibold border-gold-500';
              // Item inactivo: texto crema sobre dark, texto morado oscuro
              // sobre light. Hover empuja a gold/primary-700 respectivamente.
              const inactiveClasses = isOverDark
                ? 'text-cream-50 border-transparent hover:text-gold-300 hover:border-gold-500/50'
                : 'text-primary-900 border-transparent hover:text-primary-700 hover:border-gold-500/40';
              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'inline-flex items-center px-3 py-2',
                      'text-sm font-body transition-colors duration-2 ease-out',
                      // Línea inferior siempre presente, transparente cuando inactivo
                      // — así el hover no provoca jump vertical.
                      'border-b-2 pb-1',
                      active ? activeClasses : inactiveClasses,
                    )}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Bloque derecho: acciones rápidas + CTA + hamburguesa mobile. */}
          <div className="flex items-center gap-1 md:gap-2">
            {/* Búsqueda — solo desktop (en mobile lo asume el sheet en F8).
                Touch target >= 44x44 (WCAG 2.5.5). */}
            <button
              type="button"
              onClick={handleSearchClick}
              aria-label="Abrir búsqueda"
              className={cn(
                'hidden min-[1360px]:inline-flex items-center justify-center',
                'h-11 w-11 rounded-pill',
                'transition-colors duration-2 ease-out',
                iconBtnClass,
              )}
            >
              <Search className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />
            </button>

            {/* Icono cuenta — solo desktop. Touch target >= 44x44. */}
            <Link
              to={Routes.account}
              aria-label="Ir a mi cuenta"
              className={cn(
                'hidden min-[1360px]:inline-flex items-center justify-center',
                'h-11 w-11 rounded-pill',
                'transition-colors duration-2 ease-out',
                iconBtnClass,
              )}
            >
              <User className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />
            </Link>

            {/* Carrito con badge dinámico — visible en desktop y mobile.
                El componente respeta `isOverDark` vía data-attribute heredado:
                aquí pasamos el color base como prop CSS-friendly. */}
            <CartIconBadge tone={isOverDark ? 'dark-bg' : 'light-bg'} />

            {/* CTA reservar — sólo desktop. El sheet mobile lo incluye dentro.
                Dark mode: outline crema con border dorado (referencia visual
                del cliente — botón etéreo sobre el hero violeta). Light mode:
                botón sólido violeta `primary` estándar. */}
            <div className="hidden min-[1360px]:block ml-1">
              <Button
                type="button"
                onClick={() => setIsReservaOpen(true)}
                variant="primary"
                size="md"
                leftIcon={<Sparkles aria-hidden="true" strokeWidth={1.5} />}
                className={cn(
                  isOverDark &&
                    cn(
                      'bg-transparent border border-gold-500/70 text-cream-50',
                      'hover:bg-gold-500/15 hover:border-gold-500',
                    ),
                )}
              >
                Reservar cita
              </Button>
            </div>

            {/* Hamburguesa — sólo mobile. Touch target >= 44x44 (WCAG 2.5.5).
                `shrink-0` evita que el flex parent la achique o la expulse
                del viewport cuando el contenido es estrecho — garantiza que
                la hamburguesa siempre se ve y es tappeable. */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              className={cn(
                'inline-flex shrink-0 items-center justify-center min-[1360px]:hidden',
                'h-11 w-11 rounded-md',
                'transition-colors duration-2 ease-out',
                iconBtnClass,
              )}
              aria-label="Abrir menú de navegación"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <Menu className="h-6 w-6" aria-hidden="true" strokeWidth={1.5} />
            </button>
          </div>
        </nav>
      </header>

      {/* Sheet mobile fuera del flujo del header para evitar stacking issues.
          `onOpenSearch` permite al sheet mobile abrir el GlobalSearchModal
          después de cerrarse a sí mismo (handoff coordinado). */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onOpenSearch={() => setIsSearchOpen(true)}
        onReservar={() => setIsReservaOpen(true)}
        items={items}
      />

      {/* Command palette global — vive en portal a document.body. Se monta
          condicionalmente desde el padre (no internamente con
          `if (!isOpen) return null`) para evitar que los 4 hooks de query
          (`useTherapies`, `useProducts`, `useEvents`, `useGalleryItems`)
          se ejecuten en cada página del sitio mientras el modal está
          cerrado. Sin este guard, el Navbar dispararía 4 requests a
          Supabase por navegación en TODAS las rutas. */}
      {isSearchOpen && (
        <GlobalSearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      )}

      {/* "Reservar cita" (escritorio y menú hamburguesa): reserva de la
          Orientación gratuita en la página actual. No descarga el wizard ni
          consulta nada hasta que se abre por primera vez. */}
      <OrientacionBookingLauncher open={isReservaOpen} onClose={() => setIsReservaOpen(false)} />
    </>
  );
}
